/**
 * Arthaus Email hooks.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useToast } from '@/hooks/use-toast'
import { inboxApi } from '../api/inboxApi'
import type { Folder, InboxView, OutgoingMail } from '../types'

export const inboxKeys = {
  all: ['arthaus-email'] as const,
  state: () => [...inboxKeys.all, 'state'] as const,
  threads: (view: InboxView, q = '') => [...inboxKeys.all, 'threads', view, q] as const,
  thread: (id: number) => [...inboxKeys.all, 'thread', id] as const,
}

function errorMessage(err: unknown): string {
  const e = err as { response?: { data?: { message?: string } }; message?: string }
  return e?.response?.data?.message || e?.message || 'Something went wrong'
}

export function useInboxState() {
  return useQuery({ queryKey: inboxKeys.state(), queryFn: inboxApi.getState })
}

export function useThreads(view: InboxView, enabled: boolean, q = '') {
  return useQuery({
    queryKey: inboxKeys.threads(view, q),
    queryFn: () => inboxApi.listThreads(view, q),
    enabled,
    // New mail arrives by webhook; a light poll keeps an open inbox current without a socket.
    refetchInterval: 30000,
  })
}

export function useThread(id: number | null) {
  const queryClient = useQueryClient()
  return useQuery({
    queryKey: inboxKeys.thread(id ?? 0),
    queryFn: async () => {
      const detail = await inboxApi.getThread(id as number)
      // Opening marks it read; refresh the unread counts.
      queryClient.invalidateQueries({ queryKey: inboxKeys.state() })
      return detail
    },
    enabled: !!id,
  })
}

/** Invalidate everything inbox-shaped after a write. */
function useRefresh() {
  const queryClient = useQueryClient()
  return () => queryClient.invalidateQueries({ queryKey: inboxKeys.all })
}

export function useClaimInbox() {
  const refresh = useRefresh()
  const { toast } = useToast()
  return useMutation({
    mutationFn: inboxApi.claim,
    onSuccess: (inbox) => {
      refresh()
      toast({ title: 'Your inbox is live', description: `${inbox.emailAddress} is ready to receive mail.` })
    },
    onError: (err) => toast({ title: 'Could not claim that address', description: errorMessage(err), variant: 'destructive' }),
  })
}

export function useInboxSettings() {
  const refresh = useRefresh()
  const { toast } = useToast()
  return useMutation({
    mutationFn: inboxApi.updateSettings,
    onSuccess: refresh,
    onError: (err) => toast({ title: 'Settings not saved', description: errorMessage(err), variant: 'destructive' }),
  })
}

export function useSyncInbox() {
  const refresh = useRefresh()
  const { toast } = useToast()
  return useMutation({
    mutationFn: inboxApi.sync,
    onSuccess: (r) => {
      refresh()
      toast({ title: r.synced ? `${r.synced} conversation${r.synced === 1 ? '' : 's'} updated` : 'Up to date' })
    },
    onError: (err) => toast({ title: 'Sync failed', description: errorMessage(err), variant: 'destructive' }),
  })
}

export function useCompose() {
  const refresh = useRefresh()
  const { toast } = useToast()
  return useMutation({
    mutationFn: (mail: OutgoingMail) => inboxApi.compose(mail),
    onSuccess: () => {
      refresh()
      toast({ title: 'Email sent' })
    },
    onError: (err) => toast({ title: 'Email not sent', description: errorMessage(err), variant: 'destructive' }),
  })
}

export function useForward() {
  const refresh = useRefresh()
  const { toast } = useToast()
  return useMutation({
    mutationFn: ({ id, mail }: { id: number; mail: OutgoingMail }) => inboxApi.forward(id, mail),
    onSuccess: () => {
      refresh()
      toast({ title: 'Forwarded' })
    },
    onError: (err) => toast({ title: 'Not forwarded', description: errorMessage(err), variant: 'destructive' }),
  })
}

export function useMarkRead() {
  const refresh = useRefresh()
  return useMutation({
    mutationFn: ({ id, read }: { id: number; read: boolean }) => inboxApi.markRead(id, read),
    onSuccess: refresh,
  })
}

export function useReply() {
  const refresh = useRefresh()
  const { toast } = useToast()
  return useMutation({
    mutationFn: ({ id, mail }: { id: number; mail: OutgoingMail }) => inboxApi.reply(id, mail),
    onSuccess: () => {
      refresh()
      toast({ title: 'Reply sent' })
    },
    onError: (err) => toast({ title: 'Reply not sent', description: errorMessage(err), variant: 'destructive' }),
  })
}

export function useSaveDraft() {
  const refresh = useRefresh()
  return useMutation({
    mutationFn: ({ id, text }: { id: number; text: string }) => inboxApi.saveDraft(id, text),
    onSuccess: refresh,
  })
}

export function useRetriage() {
  const refresh = useRefresh()
  const { toast } = useToast()
  return useMutation({
    mutationFn: (id: number) => inboxApi.retriage(id),
    onSuccess: refresh,
    onError: (err) => toast({ title: 'Triage failed', description: errorMessage(err), variant: 'destructive' }),
  })
}

export function useMoveThread() {
  const refresh = useRefresh()
  const { toast } = useToast()
  return useMutation({
    mutationFn: ({ id, folder }: { id: number; folder: Folder }) => inboxApi.move(id, folder),
    onSuccess: (_r, { folder }) => {
      refresh()
      const label = { inbox: 'Moved to Inbox', archive: 'Archived', spam: 'Marked as spam', trash: 'Moved to Trash' }[folder]
      toast({ title: label })
    },
    onError: (err) => toast({ title: 'Could not move it', description: errorMessage(err), variant: 'destructive' }),
  })
}

export function useConvertToCommission() {
  const refresh = useRefresh()
  const queryClient = useQueryClient()
  const { toast } = useToast()
  return useMutation({
    mutationFn: ({ id, title }: { id: number; title?: string }) => inboxApi.convertToCommission(id, title),
    onSuccess: (r) => {
      refresh()
      queryClient.invalidateQueries({ queryKey: ['commissions'] })
      toast({ title: `Commission ${r.commission.reference} created`, description: 'The conversation now lives on the commission too.' })
    },
    onError: (err) => toast({ title: 'Could not create the commission', description: errorMessage(err), variant: 'destructive' }),
  })
}
